// web/src/onboarding/DashboardWelcome.tsx
//
// Shown once on a user's first arrival at /dashboard. Orients them to
// the parts of the dashboard that aren't immediately self-explanatory
// (the "since last run" wall, the refresh button's cooldown, the AI
// Copilot trigger). Dismissed permanently after the first viewing via
// a localStorage flag.
//
// Intentionally lightweight: no tour, no element highlighting, no
// step navigation. If we later see (via real user feedback) that
// people miss specific features, we'd graduate to a per-step tour —
// but for v1 a single dialog is the right scope.
import { useEffect, useState } from "react";
import { LineChart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "tapelens.dashboard_welcomed";

function readSeen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* sandboxed / private mode — the prompt comes back next visit, which is
       fine. We prefer "show too often" over "never persist" here. */
  }
}

interface BulletProps {
  title: string;
  body: string;
}

function Bullet({ title, body }: BulletProps) {
  return (
    <li className="flex gap-3">
      <span aria-hidden="true" className="mt-1 text-primary">●</span>
      <span>
        <strong className="font-medium">{title}</strong> {body}
      </span>
    </li>
  );
}

export function DashboardWelcome() {
  // Check on mount only. The dialog never re-opens within a session
  // once the user has dismissed — closing for any reason (CTA, Escape,
  // overlay click) counts as dismissal.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!readSeen()) setOpen(true);
  }, []);

  function handleOpenChange(next: boolean) {
    if (!next) markSeen();
    setOpen(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LineChart className="h-5 w-5 text-primary" aria-hidden="true" />
            Welcome to Tapelens
          </DialogTitle>
          <DialogDescription>
            Here's what to look at on your dashboard.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-3 text-sm">
          <Bullet
            title="Pick what you trade."
            body="The dropdown in each pane lets you switch between instruments — FX, gold, oil, BTC, DXY."
          />
          <Bullet
            title="Top of each pane:"
            body="the LLM's current directional bias and conviction, with the top drivers behind it."
          />
          <Bullet
            title="The pulsing wall"
            body='shows clusters that arrived after the last analysis run — the model hasn’t seen these yet, useful when you need to react before the next run.'
          />
          <Bullet
            title="The refresh button"
            body="forces a new analysis run (90-second cooldown between manual refreshes)."
          />
          <Bullet
            title="Ask Tapelens"
            body='sits bottom-right. Ask "why is gold moving?" or "what changed for EURUSD?" — every answer cites the source article.'
          />
        </ul>
        <p className="text-xs text-muted-foreground">
          Press{" "}
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
            ?
          </kbd>{" "}
          anytime for the keyboard shortcuts cheat sheet.
        </p>
        <DialogFooter>
          <Button
            onClick={() => {
              markSeen();
              setOpen(false);
            }}
          >
            Got it, let me in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
